import React, { useState, useCallback, useEffect } from 'react';
import MobileBottomSheet from './MobileBottomSheet';
import Effect3DControls from '../Effect3DControls';

export default function MobilePropertySheet({ isOpen, onClose, activeObject, canvasManager, bgRemoval }) {
  const [fill, setFill] = useState('#000000');
  const [stroke, setStroke] = useState('');
  const [strokeWidth, setStrokeWidth] = useState(0);
  const [opacity, setOpacity] = useState(1);
  const [fontSize, setFontSize] = useState(24);
  const [fontWeight, setFontWeight] = useState('normal');
  const [fontStyle, setFontStyle] = useState('normal');
  const [textAlign, setTextAlign] = useState('left');
  const [bgRemoving, setBgRemoving] = useState(false);
  const [bgProgress, setBgProgress] = useState('');

  const isText = activeObject ? ['i-text', 'text', 'textbox'].includes(activeObject.type) : false;
  const isImage = activeObject ? activeObject.type === 'image' : false;

  useEffect(() => {
    if (!activeObject) return;
    setFill(typeof activeObject.fill === 'string' ? activeObject.fill : '#000000');
    setStroke(activeObject.stroke || '');
    setStrokeWidth(activeObject.strokeWidth || 0);
    setOpacity(activeObject.opacity ?? 1);
    if (isText) {
      setFontSize(activeObject.fontSize || 24);
      setFontWeight(activeObject.fontWeight || 'normal');
      setFontStyle(activeObject.fontStyle || 'normal');
      setTextAlign(activeObject.textAlign || 'left');
    }
  }, [activeObject, isText]);

  const update = useCallback((prop, value) => {
    if (!activeObject || !canvasManager?.canvas) return;
    activeObject.set(prop, value);
    // For text: ensure stroke renders behind fill
    if ((prop === 'stroke' || prop === 'strokeWidth') && isText) {
      if (activeObject.stroke && activeObject.strokeWidth > 0) {
        activeObject.set('paintFirst', 'stroke');
        activeObject.set('strokeLineJoin', 'round');
        activeObject.set('strokeLineCap', 'round');
      }
    }
    // For groups/SVGs: also apply fill/stroke to children
    if ((prop === 'fill' || prop === 'stroke') && activeObject.type === 'group' && activeObject._objects) {
      activeObject._objects.forEach((child) => {
        if (child.fill && child.fill !== 'none' && child.fill !== 'transparent' && prop === 'fill') {
          child.set('fill', value);
        }
        if (prop === 'stroke') {
          child.set('stroke', value);
        }
      });
    }
    canvasManager.canvas.renderAll();
    canvasManager.canvas.fire('object:modified', { target: activeObject });
  }, [activeObject, canvasManager, isText]);

  const rowStyle = { display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px' };
  const labelStyle = { fontSize: '12px', fontWeight: 500, width: '70px', flexShrink: 0, opacity: 0.6, color: 'var(--selenny-text)' };

  return (
    <MobileBottomSheet isOpen={isOpen} onClose={onClose} title="Eigenschaften" height="45vh" noBackdrop>
      {!activeObject ? (
        <div style={{ textAlign: 'center', padding: '24px', opacity: 0.5, fontSize: '13px', color: 'var(--selenny-text)' }}>
          Kein Objekt ausgewählt
        </div>
      ) : (
        <>
          {/* Fill color (not for images) */}
          {!isImage && (
            <div style={rowStyle}>
              <span style={labelStyle}>Farbe</span>
              <input
                type="color"
                value={typeof fill === 'string' ? fill : '#000000'}
                onChange={(e) => { setFill(e.target.value); update('fill', e.target.value); }}
                style={{ width: '44px', height: '44px', border: 'none', borderRadius: '8px', cursor: 'pointer', padding: 0 }}
              />
              <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', flex: 1 }}>
                {['#000000', '#ffffff', '#ef4444', '#f97316', '#eab308', '#22c55e', '#3b82f6', '#8b5cf6'].map((c) => (
                  <button
                    key={c}
                    onClick={() => { setFill(c); update('fill', c); }}
                    style={{
                      width: 24, height: 24, borderRadius: '6px', border: fill === c ? '2px solid var(--selenny-primary)' : '1px solid var(--selenny-border)',
                      background: c, cursor: 'pointer', padding: 0,
                    }}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Stroke */}
          <div style={rowStyle}>
            <span style={labelStyle}>Rahmen</span>
            <input
              type="color"
              value={stroke || '#000000'}
              onChange={(e) => { setStroke(e.target.value); update('stroke', e.target.value); if (!strokeWidth) { setStrokeWidth(2); update('strokeWidth', 2); } }}
              style={{ width: '44px', height: '44px', border: 'none', borderRadius: '8px', cursor: 'pointer', padding: 0 }}
            />
            <input
              type="range"
              min="0" max="80" value={Math.min(strokeWidth, 80)}
              onChange={(e) => { const v = Number(e.target.value); setStrokeWidth(v); update('strokeWidth', v); }}
              style={{ flex: 1 }}
            />
            <input
              type="number" min="0" max="200" value={strokeWidth}
              onChange={(e) => { const v = Math.max(0, Math.min(200, Number(e.target.value) || 0)); setStrokeWidth(v); update('strokeWidth', v); }}
              style={{ width: '54px', fontSize: '12px', textAlign: 'right', padding: '4px 6px', border: '1px solid var(--selenny-border)', borderRadius: '6px', background: 'var(--selenny-card)', color: 'var(--selenny-text)' }}
            />
            <span style={{ fontSize: '11px', color: 'var(--selenny-text-tertiary)' }}>px</span>
          </div>

          {/* Opacity */}
          <div style={rowStyle}>
            <span style={labelStyle}>Deckkraft</span>
            <input
              type="range"
              min="0" max="1" step="0.05" value={opacity}
              onChange={(e) => { const v = Number(e.target.value); setOpacity(v); update('opacity', v); }}
              style={{ flex: 1 }}
            />
            <span style={{ fontSize: '12px', width: '35px', textAlign: 'right', color: 'var(--selenny-text)' }}>{Math.round(opacity * 100)}%</span>
          </div>

          {/* Text-specific controls */}
          {isText && (
            <>
              <div style={{ borderTop: '1px solid var(--selenny-border)', paddingTop: '14px', marginBottom: '14px' }}>
                <div style={rowStyle}>
                  <span style={labelStyle}>Größe</span>
                  <input
                    type="number"
                    value={Math.round(fontSize)}
                    onChange={(e) => { const v = Number(e.target.value); setFontSize(v); update('fontSize', v); }}
                    min="8" max="500"
                    style={{
                      width: '70px', padding: '8px', border: '1px solid var(--selenny-border)',
                      borderRadius: '8px', fontSize: '16px', textAlign: 'center',
                      background: 'var(--selenny-surface)', color: 'var(--selenny-text)',
                    }}
                  />
                  <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                    {[16, 24, 36, 48, 72, 96].map((s) => (
                      <button
                        key={s}
                        onClick={() => { setFontSize(s); update('fontSize', s); }}
                        style={{
                          padding: '4px 8px', borderRadius: '6px', fontSize: '11px', fontWeight: 500, cursor: 'pointer',
                          border: 'none',
                          background: fontSize === s ? 'var(--selenny-primary)' : 'var(--selenny-bg)',
                          color: fontSize === s ? '#fff' : 'var(--selenny-text)',
                        }}
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Bold / Italic */}
                <div style={rowStyle}>
                  <span style={labelStyle}>Stil</span>
                  <div style={{ display: 'flex', gap: '6px' }}>
                    <button
                      onClick={() => { const v = fontWeight === 'bold' ? 'normal' : 'bold'; setFontWeight(v); update('fontWeight', v); }}
                      style={{
                        width: '40px', height: '40px', border: '1px solid var(--selenny-border)',
                        borderRadius: '8px', cursor: 'pointer', fontSize: '16px', fontWeight: 'bold',
                        background: fontWeight === 'bold' ? 'var(--selenny-primary)' : 'var(--selenny-surface)',
                        color: fontWeight === 'bold' ? '#fff' : 'var(--selenny-text)',
                      }}
                    >
                      B
                    </button>
                    <button
                      onClick={() => { const v = fontStyle === 'italic' ? 'normal' : 'italic'; setFontStyle(v); update('fontStyle', v); }}
                      style={{
                        width: '40px', height: '40px', border: '1px solid var(--selenny-border)',
                        borderRadius: '8px', cursor: 'pointer', fontSize: '16px', fontStyle: 'italic',
                        background: fontStyle === 'italic' ? 'var(--selenny-primary)' : 'var(--selenny-surface)',
                        color: fontStyle === 'italic' ? '#fff' : 'var(--selenny-text)',
                      }}
                    >
                      I
                    </button>
                  </div>
                </div>

                {/* Text alignment */}
                <div style={rowStyle}>
                  <span style={labelStyle}>Ausrichtung</span>
                  <div style={{ display: 'flex', gap: '6px' }}>
                    {[
                      { key: 'left', lines: [18, 14, 16, 12] },
                      { key: 'center', lines: [14, 18, 12, 16] },
                      { key: 'right', lines: [16, 12, 18, 14] },
                    ].map((a) => (
                      <button
                        key={a.key}
                        onClick={() => { setTextAlign(a.key); update('textAlign', a.key); }}
                        style={{
                          width: '40px', height: '40px', border: '1px solid var(--selenny-border)',
                          borderRadius: '8px', cursor: 'pointer', display: 'flex', flexDirection: 'column',
                          alignItems: a.key === 'left' ? 'flex-start' : a.key === 'right' ? 'flex-end' : 'center',
                          justifyContent: 'center', gap: '3px', padding: '8px',
                          background: textAlign === a.key ? 'var(--selenny-primary)' : 'var(--selenny-surface)',
                        }}
                      >
                        {a.lines.map((w, i) => (
                          <div key={i} style={{ height: 2, width: w, borderRadius: 1, background: textAlign === a.key ? '#fff' : 'var(--selenny-text)', opacity: 0.6 }} />
                        ))}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </>
          )}

          {/* KI-Freisteller (nur bei Bildern) */}
          {isImage && bgRemoval && (
            <div style={{ marginTop: '4px', paddingTop: '14px', borderTop: '1px solid var(--selenny-border)' }}>
              <span style={{ fontSize: '10px', fontWeight: 600, color: 'var(--selenny-text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>KI-Freisteller</span>
              <button
                onClick={async () => {
                  if (!activeObject || activeObject.type !== 'image' || bgRemoving) return;
                  setBgRemoving(true);
                  setBgProgress('Modell wird geladen...');
                  try {
                    await bgRemoval.removeBackground(activeObject, (key, current, total) => {
                      if (key === 'downloading') {
                        setBgProgress('KI-Modell laden: ' + Math.round((current / total) * 100) + '%');
                      } else {
                        setBgProgress('Hintergrund wird entfernt...');
                      }
                    });
                    setBgProgress('');
                  } catch (err) {
                    console.error('BG removal failed:', err);
                  } finally {
                    setBgRemoving(false);
                  }
                }}
                disabled={bgRemoving}
                style={{
                  width: '100%', marginTop: '8px', padding: '12px', border: 'none', borderRadius: '10px',
                  cursor: bgRemoving ? 'wait' : 'pointer', fontSize: '13px', fontWeight: 500,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                  background: bgRemoving ? 'var(--selenny-bg)' : '#f3e8ff',
                  color: bgRemoving ? 'var(--selenny-text-muted)' : '#7c3aed',
                }}
              >
                {bgRemoving ? bgProgress : 'Hintergrund entfernen (KI)'}
              </button>
            </div>
          )}

          {/* 3D-Effekt-Einstellungen */}
          <div style={{ marginTop: '4px', paddingTop: '10px', borderTop: '1px solid var(--selenny-border)' }}>
            <Effect3DControls activeObject={activeObject} canvasManager={canvasManager} />
          </div>

          {/* Actions: Lock, Group, Duplicate & Delete */}
          <div style={{ display: 'flex', gap: '8px', marginTop: '4px', paddingTop: '14px', borderTop: '1px solid var(--selenny-border)', flexWrap: 'wrap' }}>
            {/* Lock / Unlock */}
            <button
              onClick={() => { canvasManager?.toggleLock?.(); }}
              style={{
                flex: '1 1 45%', padding: '12px', border: '1px solid var(--selenny-border)',
                borderRadius: '8px', cursor: 'pointer',
                fontSize: '13px', fontWeight: 500, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
                background: activeObject?.isLocked ? '#fffbeb' : 'var(--selenny-surface)',
                color: activeObject?.isLocked ? '#b45309' : 'var(--selenny-text)',
              }}
            >
              {activeObject?.isLocked ? (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0110 0v4"/></svg>
              ) : (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 019.9-1"/></svg>
              )}
              {activeObject?.isLocked ? 'Entsperren' : 'Sperren'}
            </button>

            {/* Group (only for multi-selection) */}
            {activeObject?.type === 'activeSelection' && (
              <button
                onClick={() => { canvasManager?.groupSelected?.(); onClose(); }}
                style={{
                  flex: '1 1 45%', padding: '12px', border: '1px solid #bfdbfe',
                  borderRadius: '8px', background: '#eff6ff', cursor: 'pointer',
                  fontSize: '13px', fontWeight: 500, color: '#1d4ed8',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="2" width="8" height="8" rx="1"/><rect x="14" y="14" width="8" height="8" rx="1"/><path d="M6 14v-4h4M18 10v4h-4"/></svg>
                Gruppieren
              </button>
            )}

            {/* Ungroup (only for groups, not curved text) */}
            {activeObject?.type === 'group' && !activeObject?._curvedText && (
              <button
                onClick={() => { canvasManager?.ungroupSelected?.(); onClose(); }}
                style={{
                  flex: '1 1 45%', padding: '12px', border: '1px solid #fed7aa',
                  borderRadius: '8px', background: '#fff7ed', cursor: 'pointer',
                  fontSize: '13px', fontWeight: 500, color: '#c2410c',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="2" width="8" height="8" rx="1"/><rect x="14" y="14" width="8" height="8" rx="1"/><path d="M14 6h4v4M10 18H6v-4"/></svg>
                Auflösen
              </button>
            )}

            <button
              onClick={() => { canvasManager?.duplicateSelected?.(); onClose(); }}
              style={{
                flex: '1 1 45%', padding: '12px', border: '1px solid var(--selenny-border)',
                borderRadius: '8px', background: 'var(--selenny-surface)', cursor: 'pointer',
                fontSize: '13px', fontWeight: 500, color: 'var(--selenny-text)',
              }}
            >
              Duplizieren
            </button>
            <button
              onClick={() => { canvasManager?.deleteSelected?.(); onClose(); }}
              style={{
                flex: '1 1 45%', padding: '12px', border: '1px solid #fecaca',
                borderRadius: '8px', background: '#fef2f2', cursor: 'pointer',
                fontSize: '13px', fontWeight: 500, color: '#dc2626',
              }}
            >
              Löschen
            </button>
          </div>
        </>
      )}
    </MobileBottomSheet>
  );
}
